import { useState, useCallback, useMemo, useRef } from 'react';

const SECTION_ORDER = [
  'venue',
  'datetime',
  'inventory',
  'addons',
  'food',
  'guests',
  'review',
] as const;
type Section = (typeof SECTION_ORDER)[number];

// Per user decision: cascade dependencies
const SECTION_DEPENDENCIES: Record<Section, Section[]> = {
  venue: ['datetime', 'inventory', 'addons', 'food', 'guests', 'review'], // Changing venue resets all
  datetime: ['inventory', 'addons', 'food', 'guests', 'review'], // Changing time resets downstream
  inventory: ['addons', 'food', 'guests', 'review'], // Changing inventory resets addons/food/guests/review
  addons: ['food', 'review'], // Changing addons resets food and review (needs recalc)
  food: ['review'], // Changing food resets review (needs recalc)
  guests: ['review'], // Changing guests resets review
  review: [], // Final section, no dependents
};

interface UseBookingProgressOptions {
  initialCompletedSections?: Section[];
  initialSectionData?: Partial<Record<Section, unknown>>;
  initialSectionSummaries?: Partial<Record<Section, string>>;
  bookingMode?: 'capacity' | 'inventory';
  /** When true, the venue step is hidden from visible sections (pre-selected via URL param) */
  venuePreSelected?: boolean;
  /** Whether this venue has available food items */
  hasFoodMenu?: boolean;
  /** Whether the food-ordering feature flag is enabled */
  foodOrderingEnabled?: boolean;
}

export function useBookingProgress(options?: UseBookingProgressOptions) {
  const venuePreSelected = options?.venuePreSelected ?? false;
  const hasFoodMenu = options?.hasFoodMenu ?? false;
  const foodOrderingEnabled = options?.foodOrderingEnabled ?? false;
  const showFood = hasFoodMenu && foodOrderingEnabled;
  const [currentSection, setCurrentSection] = useState<Section>(
    options?.initialCompletedSections?.includes('venue') ? 'datetime' : 'venue'
  );
  const [completedSections, setCompletedSections] = useState<Section[]>(
    options?.initialCompletedSections || []
  );
  const [sectionData, setSectionData] = useState<Partial<Record<Section, unknown>>>(
    options?.initialSectionData || {}
  );
  const [sectionSummaries, setSectionSummaries] = useState<Partial<Record<Section, string>>>(
    options?.initialSectionSummaries || {}
  );
  const [bookingMode, setBookingMode] = useState<'capacity' | 'inventory' | undefined>(
    options?.bookingMode
  );

  // Refs so callbacks always access current values without stale closures
  const sectionDataRef = useRef(sectionData);
  sectionDataRef.current = sectionData;
  const completedSectionsRef = useRef(completedSections);
  completedSectionsRef.current = completedSections;
  const bookingModeRef = useRef(bookingMode);
  bookingModeRef.current = bookingMode;
  const showFoodRef = useRef(showFood);
  showFoodRef.current = showFood;

  const expandSection = useCallback((section: Section) => {
    setCurrentSection(section);
  }, []);

  const completeSection = useCallback(
    (section: Section, data: unknown, summary: string) => {
      const previousData = sectionDataRef.current[section];
      const wasCompleted = completedSectionsRef.current.includes(section);
      const dataChanged = JSON.stringify(previousData) !== JSON.stringify(data);

      // Only reset dependents if this section was already completed AND its data changed
      if (wasCompleted && dataChanged) {
        const dependents = SECTION_DEPENDENCIES[section];
        setCompletedSections((prev) => {
          const filtered = prev.filter((s) => !dependents.includes(s));
          return [...new Set([...filtered, section])];
        });
        setSectionData((prev) => {
          const updated = { ...prev, [section]: data };
          dependents.forEach((dep) => delete updated[dep]);
          return updated;
        });
        setSectionSummaries((prev) => {
          const updated = { ...prev, [section]: summary };
          dependents.forEach((dep) => delete updated[dep]);
          return updated;
        });
      } else {
        setSectionData((prev) => ({ ...prev, [section]: data }));
        setSectionSummaries((prev) => ({ ...prev, [section]: summary }));
        setCompletedSections((prev) => [...new Set([...prev, section])]);
      }

      // Auto-advance to next section
      // Use bookingMode from data if available (handles race condition where
      // bookingMode hasn't been set yet when datetime section completes)
      const dataBookingMode = (data as { bookingMode?: 'capacity' | 'inventory' })?.bookingMode;
      const effectiveMode = dataBookingMode ?? bookingModeRef.current;

      const foodStep: Section[] = showFoodRef.current ? ['food'] : [];
      const effectiveOrder: Section[] =
        effectiveMode === 'capacity'
          ? (['venue', 'datetime', 'addons', ...foodStep, 'guests', 'review'] as Section[])
          : (['venue', 'datetime', 'inventory', ...foodStep, 'guests', 'review'] as Section[]);

      const currentIdx = effectiveOrder.indexOf(section);
      if (currentIdx < effectiveOrder.length - 1) {
        setCurrentSection(effectiveOrder[currentIdx + 1]);
      }
    },
    []
  );

  // Derived: which sections are visible (progressive disclosure)
  // Addons and Guest Details are optional - Review shows after datetime completion
  const visibleSections = useMemo(() => {
    // When venue is pre-selected (venueId in URL), hide the venue step entirely
    const visible: Section[] = venuePreSelected ? [] : ['venue'];

    // Show datetime after venue
    if (completedSections.includes('venue')) {
      visible.push('datetime');
    }

    // Show inventory step only in inventory mode, after datetime
    if (completedSections.includes('datetime') && bookingMode === 'inventory') {
      visible.push('inventory');
    }

    // Show addons and guests after datetime (capacity mode) or after inventory (inventory mode)
    const datetimeDone = completedSections.includes('datetime');
    const inventoryDone = completedSections.includes('inventory');
    const canShowAddonsAndGuests = datetimeDone && (bookingMode !== 'inventory' || inventoryDone);

    if (canShowAddonsAndGuests) {
      if (bookingMode !== 'inventory') {
        visible.push('addons'); // Only for capacity mode; inventory mode embeds addons in inventory section
      }
      if (showFood) {
        visible.push('food');
      }
      visible.push('guests');
      // Show review after the required steps are complete
      visible.push('review');
    } else if (datetimeDone && bookingMode === 'inventory') {
      // In inventory mode, show inventory section but not guests/review yet
    }

    return visible;
  }, [completedSections, bookingMode, venuePreSelected, showFood]);

  return {
    currentSection,
    completedSections,
    sectionData,
    sectionSummaries,
    visibleSections,
    bookingMode,
    setBookingMode,
    expandSection,
    completeSection,
  };
}
